/**
 * BingX timeframe configuration and interval mapping.
 * @see https://bingx-api.github.io/docs/#/en-us/spot/market-api
 */

import {
  registerExchangeTimeframes,
  resolutionToSeconds,
  type TimeframeConfig,
} from '../../lib/timeframes';

/**
 * BingX supports 1-minute minimum.
 * @see https://bingx-api.github.io/docs/#/en-us/spot/market-api.html#Kline/Candlestick%20Data
 */
export const BINGX_TIMEFRAMES: TimeframeConfig = {
  native: [
    '1m',
    '3m',
    '5m',
    '15m',
    '30m',
    '1h',
    '2h',
    '4h',
    '6h',
    '8h',
    '12h',
    '1d',
    '3d',
    '1w',
    '1M',
  ],
  synthetic: ['1s'], // Use ticker for 1s
  minIntervalSeconds: 60,
};

// Register with central registry
registerExchangeTimeframes('bingx', BINGX_TIMEFRAMES);

/**
 * Maps TradingView resolution to BingX WebSocket interval format.
 * @see https://bingx-api.github.io/docs/#/en-us/spot/socket/market.html#K-line%20Streams
 *
 * WebSocket uses long format: 1min, 60min, 2hour, 4hour, 1day, 1week, 1mon
 */
export const mapToBingxWsInterval = (resolution: string): string => {
  const seconds = resolutionToSeconds(resolution);

  if (seconds === 60) return '1min';
  if (seconds === 180) return '3min';
  if (seconds === 300) return '5min';
  if (seconds === 900) return '15min';
  if (seconds === 1800) return '30min';
  if (seconds === 3600) return '60min';
  if (seconds === 7200) return '2hour';
  if (seconds === 14400) return '4hour';
  if (seconds === 21600) return '6hour';
  if (seconds === 28800) return '8hour';
  if (seconds === 43200) return '12hour';
  if (seconds === 86400) return '1day';
  if (seconds === 259200) return '3day';
  if (seconds === 604800) return '1week';
  if (seconds === 2592000) return '1mon';

  // Default to closest
  if (seconds < 60) return '1min';
  if (seconds < 180) return '1min';
  return '60min';
};

/**
 * Maps TradingView resolution to BingX REST API interval format.
 * @see https://bingx-api.github.io/docs/#/en-us/spot/market-api.html#Kline/Candlestick%20Data
 *
 * REST uses short format: 1m, 1h, 2h, 4h, 1d, 1w, 1M
 */
export const mapToBingxRestInterval = (resolution: string): string => {
  const seconds = resolutionToSeconds(resolution);

  if (seconds === 60) return '1m';
  if (seconds === 180) return '3m';
  if (seconds === 300) return '5m';
  if (seconds === 900) return '15m';
  if (seconds === 1800) return '30m';
  if (seconds === 3600) return '1h';
  if (seconds === 7200) return '2h';
  if (seconds === 14400) return '4h';
  if (seconds === 21600) return '6h';
  if (seconds === 28800) return '8h';
  if (seconds === 43200) return '12h';
  if (seconds === 86400) return '1d';
  if (seconds === 259200) return '3d';
  if (seconds === 604800) return '1w';
  if (seconds === 2592000) return '1M';

  // Default to closest
  if (seconds < 60) return '1m';
  if (seconds < 180) return '1m';
  return '1h';
};

/**
 * Alias for mapToBingxWsInterval for backward compatibility.
 * Used by WebSocket subscription code.
 * @deprecated Use mapToBingxWsInterval or mapToBingxRestInterval explicitly
 */
export const mapToBingxInterval = mapToBingxWsInterval;
